// ────────────────────────────────────────────────────────────────────────────
// BÉNÉFICE / MARGE — SOURCE UNIQUE DE VÉRITÉ POUR TOUT L'ERP
//
// Toute somme d'argent liée au bénéfice (ventes, caisse, commissions
// représentants, commissions pro) DOIT passer par ces fonctions. Ne JAMAIS
// recopier la formule ailleurs.
//
// Règle métier (validée avec Khalil) :
//
//   bénéfice ligne = (prixVenteNetHT − coûtUnitaireNetHT) × quantité
//
//   • prixVenteNetHT     = unitPriceHt × (1 − discountPct/100)
//                          (prix de vente HT après remise commerciale)
//   • coûtUnitaireNetHT  = purchasePriceHt × (1 − supplierDiscount/100)
//                          (prix d'achat HT NET de la remise fournisseur)
//
// 1. COÛT NET — on déduit toujours la remise fournisseur. C'est le coût réel
//    d'acquisition, identique à celui affiché sur la fiche article.
// 2. SNAPSHOT — le coût net est figé sur la ligne au moment de la vente. Le
//    bénéfice d'un document enregistré ne change jamais si le prix d'achat de
//    l'article évolue ensuite. Les appelants passent le coût figé via
//    `unitCostHt` ; le catalogue (`purchasePriceHt` + `supplierDiscount`) n'est
//    qu'un repli quand aucun snapshot n'existe.
// 3. COÛT INCONNU — une ligne sans coût connu (≤ 0) est EXCLUE du total et
//    signalée via `hasKnownPurchasePrice`. On ne compte JAMAIS une vente comme
//    100 % de bénéfice faute de coût (sinon on surévalue le bénéfice et les
//    commissions — risque financier).
// ────────────────────────────────────────────────────────────────────────────

import { calculateLineTotals } from './calculate-line-totals';

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(n, 0), 100);
}

/** Champs catalogue nécessaires pour dériver le coût net d'un article. */
export interface ArticleCostInput {
  /** Prix d'achat brut HT. */
  purchasePriceHt?: number | null;
  /** Remise fournisseur en % (0–100). */
  supplierDiscount?: number | null;
}

/**
 * Coût unitaire NET HT = prix d'achat brut HT × (1 − remise fournisseur/100).
 *
 * Retourne `null` si le prix d'achat brut est inconnu (≤ 0) : on ne devine
 * jamais un coût. C'est l'unique endroit où la remise fournisseur est appliquée.
 */
/**
 * Coût de référence d'une ligne vendue ou achetée en DÉCLINAISON : le prix
 * d'achat propre de la variante quand elle en a un, celui de l'article sinon.
 * La remise fournisseur reste celle de l'article dans les deux cas — c'est un
 * terme négocié avec le fournisseur, pas une propriété d'une couleur ou d'une
 * taille.
 */
export function variantCostInput(
  article: ArticleCostInput,
  variant?: { purchasePriceHt?: number | null } | null,
): ArticleCostInput {
  if (variant?.purchasePriceHt == null) return article;
  return {
    purchasePriceHt: variant.purchasePriceHt,
    supplierDiscount: article.supplierDiscount,
  };
}

export function netUnitCostHt(article: ArticleCostInput | null | undefined): number | null {
  const gross = Number(article?.purchasePriceHt);
  if (!Number.isFinite(gross) || gross <= 0) return null;
  const discount = clampPct(Number(article?.supplierDiscount) || 0);
  return round3(gross * (1 - discount / 100));
}

export interface SalesBenefitLineInput {
  /** Optionnel — conservé pour compat ; le calcul ne dépend que du coût. */
  articleId?: string | null;
  quantity: number;
  unitPriceHt: number;
  /**
   * Ancre TTC de la ligne (voir DocumentLineInput.unitPriceTtc). Quand elle
   * est présente, le revenu HT est le HT DÉRIVÉ du TTC — il faut alors aussi
   * fournir `vatRate` pour que la dérivation soit correcte.
   */
  unitPriceTtc?: number | null;
  /** Taux de TVA de la ligne — requis seulement pour les lignes ancrées TTC. */
  vatRate?: number | null;
  discountPct?: number | null;
  /** 'pct' (défaut) ou 'amount' — comment la remise ligne est exprimée. */
  discountMode?: 'pct' | 'amount' | null;
  /** Montant de la remise ligne en DT, utilisé quand discountMode === 'amount'. */
  discountAmount?: number | null;
  /**
   * Coût unitaire net HT figé sur la ligne (snapshot). PRIORITAIRE quand > 0.
   */
  unitCostHt?: number | null;
  /** Repli catalogue (si pas de snapshot) : prix d'achat brut HT. */
  purchasePriceHt?: number | null;
  /** Repli catalogue : remise fournisseur en % (appliquée au prix d'achat brut). */
  supplierDiscount?: number | null;
}

export interface SalesLineBenefit {
  benefit: number;
  /** Coût unitaire net HT retenu, ou `null` si inconnu. */
  unitCostHt: number | null;
  /** `true` seulement si un coût net a pu être déterminé. */
  hasKnownPurchasePrice: boolean;
  /**
   * Revenu net HT de la ligne (= « Total ligne HT »), calculé même quand le
   * coût est inconnu — sert à répartir la remise globale document au prorata.
   */
  revenueHt: number;
}

export interface SalesDocumentBenefit {
  totalBenefit: number;
  hasKnownPurchasePrice: boolean;
}

export interface SalesDocumentBenefitOptions {
  /**
   * Remise GLOBALE document en montant HT (colonne `document_discount`). Elle
   * réduit le revenu, pas le coût → elle est déduite du bénéfice, au prorata
   * de la part de revenu des lignes à coût connu (une ligne exclue du bénéfice
   * n'absorbe pas la remise des autres).
   */
  documentDiscountHt?: number | null;
}

/**
 * Forme structurelle d'une ligne de document de vente (enregistrée ou en cours
 * de saisie) telle que la portent les apps — sous-ensemble de
 * `SalesDocumentLine` (interfaces data-access), sans en dépendre.
 */
export interface SalesLineLike {
  articleId?: string | null;
  quantity: number;
  unitPriceHt: number;
  unitPriceTtc?: number | null;
  vatRate?: number | null;
  discountPct?: number | null;
  discountMode?: 'pct' | 'amount' | null;
  discountAmount?: number | null;
  /** Coût net HT FIGÉ sur la ligne (colonne `purchase_price`). */
  purchasePrice?: number | null;
}

/**
 * MAPPER CANONIQUE ligne de vente → entrée bénéfice.
 *
 * Toute surface qui calcule un bénéfice à partir d'une ligne de document DOIT
 * passer par lui : tous les bugs d'écart de bénéfice trouvés à l'audit
 * 2026-07-23 venaient de mappings manuels partiels (remise en DT ignorée car
 * `discountMode`/`discountAmount` omis, ancre TTC perdue car
 * `unitPriceTtc`/`vatRate` omis).
 *
 * Le coût est STRICTEMENT le snapshot figé de la ligne — jamais le catalogue
 * vivant : le bénéfice d'un document ENREGISTRÉ ne bouge pas quand le prix
 * d'achat change ensuite. Une ligne sans snapshot est signalée « coût
 * inconnu » et exclue (jamais estimée au catalogue du jour). Seul un
 * formulaire de saisie, pour une ligne PAS ENCORE enregistrée, peut passer un
 * coût résolu du catalogue via `opts.unitCostHt`.
 */
export function salesLineToBenefitInput(
  line: SalesLineLike,
  opts?: { vatExempt?: boolean; unitCostHt?: number | null },
): SalesBenefitLineInput {
  const vatExempt = opts?.vatExempt === true;
  return {
    articleId: line.articleId,
    quantity: Number(line.quantity) || 0,
    unitPriceHt: Number(line.unitPriceHt) || 0,
    // Exonéré : le bénéfice suit le HT réellement facturé (ancre TTC ignorée).
    unitPriceTtc: vatExempt ? 0 : Number(line.unitPriceTtc) || 0,
    vatRate: vatExempt ? 0 : Number(line.vatRate) || 0,
    discountPct: Number(line.discountPct) || 0,
    discountMode: line.discountMode ?? 'pct',
    discountAmount: Number(line.discountAmount) || 0,
    unitCostHt: opts?.unitCostHt !== undefined ? opts.unitCostHt : Number(line.purchasePrice) || 0,
  };
}

/**
 * Coût unitaire net retenu pour une ligne : le snapshot figé d'abord, sinon
 * le coût net dérivé du catalogue.
 */
export function resolveLineNetCost(line: SalesBenefitLineInput): number | null {
  const snapshot = Number(line.unitCostHt);
  if (Number.isFinite(snapshot) && snapshot > 0) return round3(snapshot);
  return netUnitCostHt({
    purchasePriceHt: line.purchasePriceHt,
    supplierDiscount: line.supplierDiscount,
  });
}

export function calculateSalesLineBenefit(line: SalesBenefitLineInput): SalesLineBenefit {
  const quantity = Number(line.quantity) || 0;
  // Revenu net HT de la ligne = EXACTEMENT le « Total ligne HT » affiché et
  // enregistré sur le document (même fonction, mêmes arrondis, mêmes bornes de
  // remise). Bénéfice et total ligne ne peuvent donc jamais diverger, même
  // d'un millime.
  // Ancre HT : le totalHt ne dépend pas du taux → TVA neutre (0). Ancre TTC :
  // le HT est dérivé du TTC, le taux réel de la ligne est indispensable.
  const anchorTtc = Number(line.unitPriceTtc);
  const isTtcAnchored = Number.isFinite(anchorTtc) && anchorTtc > 0;
  const { totalHt: netRevenueHt } = calculateLineTotals({
    quantity,
    unitPriceHt: Number(line.unitPriceHt) || 0,
    unitPriceTtc: isTtcAnchored ? anchorTtc : undefined,
    discountPct: clampPct(Number(line.discountPct) || 0),
    discountMode: line.discountMode ?? 'pct',
    discountAmount: Number(line.discountAmount) || 0,
    vatRate: isTtcAnchored ? (Number(line.vatRate) || 0) : 0,
  });

  const unitCostHt = resolveLineNetCost(line);
  if (unitCostHt == null) {
    return { benefit: 0, unitCostHt: null, hasKnownPurchasePrice: false, revenueHt: netRevenueHt };
  }

  const benefit = round3(netRevenueHt - unitCostHt * quantity);

  return { benefit, unitCostHt, hasKnownPurchasePrice: true, revenueHt: netRevenueHt };
}

export function calculateSalesDocumentBenefit(
  lines: SalesBenefitLineInput[],
  options?: SalesDocumentBenefitOptions,
): SalesDocumentBenefit {
  let totalBenefit = 0;
  let hasKnownPurchasePrice = false;
  let knownRevenueHt = 0;
  let totalRevenueHt = 0;

  for (const line of lines) {
    const lineBenefit = calculateSalesLineBenefit(line);
    totalRevenueHt += lineBenefit.revenueHt;
    if (!lineBenefit.hasKnownPurchasePrice) continue;
    totalBenefit += lineBenefit.benefit;
    knownRevenueHt += lineBenefit.revenueHt;
    hasKnownPurchasePrice = true;
  }

  // Remise globale document : réduction de revenu pur → déduite du bénéfice.
  // Au prorata de la part de revenu des lignes à coût connu, pour ne pas faire
  // porter aux lignes comptées la remise des lignes exclues (coût inconnu).
  const documentDiscountHt = Number(options?.documentDiscountHt) || 0;
  if (documentDiscountHt > 0 && hasKnownPurchasePrice && totalRevenueHt > 0) {
    const knownShare = Math.min(Math.max(knownRevenueHt / totalRevenueHt, 0), 1);
    totalBenefit -= documentDiscountHt * knownShare;
  }

  return {
    totalBenefit: round3(totalBenefit),
    hasKnownPurchasePrice,
  };
}
